import { cropImage, detectOwlWindow } from 'maplestory-owl-screenshot';
import { OWL_TRANSCRIPTION_SCHEMA_VERSION, OWL_WINDOW_HEIGHT, OWL_WINDOW_WIDTH, } from 'maplestory-owl-transcribe-contract';
import { validatePixelImage, rectInside, validRectShape } from './input.js';
import { getOwlTextTarget } from './targets.js';
import { prepareOwlScreenshotDetailed } from './prepare.js';
import { transcribePreparedOwlScreenshot } from './transcribe.js';
function failure(code, message) {
    return { ok: false, error: { code, message } };
}
function normalizeOptions(options) {
    if (options !== undefined && (!options || typeof options !== 'object')) {
        return { error: 'Options must be an object.' };
    }
    if (options) {
        const unknown = Object.keys(options).find((key) => key !== 'owlWindowHint');
        if (unknown)
            return { error: `Unknown transcription option: ${unknown}.` };
    }
    if (options?.owlWindowHint !== undefined) {
        const hint = options.owlWindowHint;
        if (!validRectShape(hint)
            || hint.width !== OWL_WINDOW_WIDTH
            || hint.height !== OWL_WINDOW_HEIGHT) {
            return { error: `owlWindowHint must be a ${OWL_WINDOW_WIDTH}×${OWL_WINDOW_HEIGHT} non-negative integer rectangle.` };
        }
    }
    return { options: options ?? {} };
}
function acceptedHint(image, hint) {
    if (!hint || !rectInside(image, hint))
        return undefined;
    const hintedImage = cropImage(image, hint);
    const detection = detectOwlWindow(hintedImage);
    if (!detection
        || detection.rect.x !== 0
        || detection.rect.y !== 0
        || detection.rect.width !== hint.width
        || detection.rect.height !== hint.height)
        return undefined;
    return hint;
}
function parseInteger(text) {
    if (!text || !/^(?:[0-9]+|[0-9]{1,3}(?:,[0-9]{3})+)$/u.test(text))
        return null;
    const value = Number(text.replaceAll(',', ''));
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
}
function targetHasInk(prepared, rowIndex) {
    for (const key of ['id', 'storeName', 'bundle', 'price', 'quantity']) {
        const image = getOwlTextTarget(prepared, key, rowIndex).image;
        const channels = image.channels ?? 4;
        const stride = image.stride ?? image.width * channels;
        for (let y = 0; y < image.height; y++) {
            for (let x = 0; x < image.width; x++) {
                const offset = y * stride + x * channels;
                if (image.data[offset] !== 255 || image.data[offset + 1] !== 255 || image.data[offset + 2] !== 255)
                    return true;
            }
        }
    }
    return false;
}
function listingFromRow(rowIndex, row) {
    return {
        rowIndex,
        sellerName: row?.id || null,
        storeName: row?.storeName || null,
        bundle: parseInteger(row?.bundle ?? ''),
        price: parseInteger(row?.price ?? ''),
        quantity: parseInteger(row?.quantity ?? ''),
    };
}
function mapTranscription(prepared) {
    const raw = transcribePreparedOwlScreenshot(prepared, { atlasVariant: prepared.atlasVariant });
    const rawRows = new Map(raw.rows.map((row) => [row.index, row]));
    const listings = [];
    for (const preparedRow of prepared.rows) {
        const row = rawRows.get(preparedRow.index);
        if (row || targetHasInk(prepared, preparedRow.index))
            listings.push(listingFromRow(preparedRow.index, row));
    }
    return {
        schemaVersion: OWL_TRANSCRIPTION_SCHEMA_VERSION,
        itemName: raw.itemName || null,
        page: raw.page ?? null,
        totalPages: raw.totalPages ?? null,
        totalResults: raw.totalRows ?? null,
        listings,
    };
}
function isPartial(transcription) {
    return transcription.itemName === null
        || transcription.page === null
        || transcription.totalPages === null
        || transcription.totalResults === null
        || transcription.listings.some((listing) => (listing.sellerName === null
            || listing.storeName === null
            || listing.bundle === null
            || listing.price === null
            || listing.quantity === null));
}
export const transcribeOwlScreenshot = (image, suppliedOptions) => {
    const optionProbe = normalizeOptions(suppliedOptions);
    if (!optionProbe.options)
        return failure('invalid-options', optionProbe.error);
    const imageProbe = validatePixelImage(image);
    if (!imageProbe.image)
        return failure('invalid-image', imageProbe.error);
    const internalImage = imageProbe.image;
    const hint = acceptedHint(internalImage, optionProbe.options.owlWindowHint);
    const preparedResult = prepareOwlScreenshotDetailed(internalImage, hint ? { windowRect: hint } : {});
    if (!preparedResult.ok) {
        return preparedResult.reason === 'owl-window-not-found'
            ? failure('owl-window-not-found', 'No supported Owl result window was found.')
            : failure('unsupported-text-rendering', 'The Owl window uses a text-rendering profile that is not supported.');
    }
    const owlWindow = preparedResult.prepared.layout.window.rect;
    const transcription = mapTranscription(preparedResult.prepared);
    return {
        ok: true,
        partial: isPartial(transcription),
        owlWindow,
        transcription,
    };
};
export const owlTranscriber = {
    transcribe: transcribeOwlScreenshot,
};
//# sourceMappingURL=public.js.map